import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Facebook, Instagram, Menu, X, Info, MessageCircle, PiggyBank } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, STEPS } from "./pqjewelriesData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const CORAL = "#D96C4F";
const GOLD = "#C79A3E";
const CREAM = "#FFF8EF";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function PqjewelriesSite() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Layaway", "#layaway"], ["Pieces", "#pieces"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#2E211A", fontFamily: "'Quicksand', sans-serif" }} data-testid="pqjewelries-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700&family=Quicksand:wght@400;500;600;700&display=swap'); .pq-heading{font-family:'Baloo 2',sans-serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: GOLD }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="pq-logo" className="flex items-center gap-3">
            <img src="/pqjewelries/logo.jpg" alt={BIZ.name} className="h-10 w-10 object-contain" />
            <span className="pq-heading text-lg font-semibold" style={{ color: CORAL }}>PQ Jewelries</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#4A3A2E] hover:text-[#D96C4F]">{l}</a>)}
          </div>
          <a href="#visit" data-testid="pq-header-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ background: CORAL }}>Visit Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="pq-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — real grand-opening storefront, fresh-shop energy */}
      <section className="relative min-h-[76vh] flex items-center" data-testid="pq-hero">
        <img src="/pqjewelries/storefront.jpg" alt="PQ Jewelries and Accessories Shop storefront" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(46,33,26,0.2) 0%, rgba(46,33,26,0.92) 82%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="rounded-full px-4 py-1.5 text-xs font-semibold" style={{ background: CORAL, color: "white" }}>Dumaguete City</span>
            <h1 className="mt-5 text-4xl sm:text-5xl pq-heading font-semibold text-white leading-[1.1]">{BIZ.tagline}</h1>
            <p className="mt-5 text-white/80 max-w-md">18K &amp; 21K gold, 92.5 silver and original branded watches, all under one roof.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#pieces" data-testid="pq-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white" style={{ background: CORAL }}>See Our Pieces</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="pq-hero-message" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-7 text-sm text-white/60">{BIZ.rating}★ Rating · {BIZ.reviewCount} Reviews</p>
          </motion.div>
        </div>
      </section>

      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.p {...fadeUp} className="text-xl leading-relaxed" style={{ color: "#5A3F2E" }}>{BIZ.intro}</motion.p>
        <motion.div {...fadeUp} className="mt-10 grid sm:grid-cols-4 gap-6">
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="text-center">
              <p className="font-semibold text-sm">{h.title}</p>
              <p className="mt-1.5 text-xs text-[#5A3F2E]/70">{h.text}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* LAYAWAY — real staff+signage proof */}
      <section id="layaway" className="py-16" style={{ background: "#FCEFE2" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div {...fadeUp}>
            <PiggyBank size={30} className="mx-auto mb-3" style={{ color: CORAL }} />
            <h2 className="pq-heading text-3xl font-semibold" style={{ color: "#5A3F2E" }}>Easy Layaway Plan</h2>
            <p className="mt-3 text-[#5A3F2E]/80 max-w-md mx-auto">Ask us about the layaway plan for bigger pieces — no need to pay it all at once.</p>
          </motion.div>
          <motion.img {...fadeUp} src="/pqjewelries/staff-counter.jpg" alt="Ask about the layaway plan at PQ Jewelries and Accessories" className="mt-8 w-full max-w-xl mx-auto shadow-md" />
        </div>
      </section>

      {/* PIECES */}
      <section id="pieces" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="pq-heading text-3xl font-semibold text-center mb-12" style={{ color: "#5A3F2E" }}>Gold, Silver &amp; Original Watches</motion.h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => (
            <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="pq-product-card" className="bg-white shadow-sm group">
              <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4">
                <h3 className="text-sm font-semibold">{p.name}</h3>
                <p className="mt-1 text-xs text-[#5A3F2E]/70">{p.desc}</p>
                <p className="mt-3 text-xs font-semibold" style={{ color: CORAL }}>Ask About This Piece &rarr;</p>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16" style={{ background: "#5A3F2E" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid sm:grid-cols-4 gap-6 text-center">
          {STEPS.map((s) => (
            <motion.div key={s.n} {...fadeUp}>
              <div className="pq-heading text-2xl font-semibold" style={{ color: GOLD }}>{s.n}</div>
              <p className="mt-2 font-semibold text-sm text-white">{s.title}</p>
              <p className="mt-1 text-xs text-white/60">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.h2 {...fadeUp} className="pq-heading text-3xl font-semibold mb-8" style={{ color: "#5A3F2E" }}>Visit or Message Us</motion.h2>
        <motion.ul {...fadeUp} className="space-y-4 inline-block text-left">
          <li className="flex items-start gap-4"><MapPin size={20} style={{ color: CORAL }} className="mt-0.5" />{BIZ.location}</li>
          <li className="flex items-start gap-4"><Facebook size={20} style={{ color: CORAL }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="pq-facebook-link" className="hover:underline">pqjwelries</a></li>
          <li className="flex items-start gap-4"><Instagram size={20} style={{ color: CORAL }} className="mt-0.5" /><a href={BIZ.instagram} target="_blank" rel="noopener noreferrer" data-testid="pq-instagram-link" className="hover:underline">See us on Instagram</a></li>
        </motion.ul>
      </section>

      <footer className="py-8 bg-white border-t" style={{ borderColor: "#F0E0D0" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#7A6252]">
          <p className="pq-heading text-base font-semibold" style={{ color: CORAL }}>PQ Jewelries &amp; Accessories</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="pq-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: CORAL }}><MessageCircle size={17} /> Message PQ Jewelries</a>
      <ConceptCTAPill slug="pq-jewelries-concept" />
    </div>
  );
}
